import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LOGO, HERO } from "../lib/constants";
import { apiErr } from "../lib/api";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/feed";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [accountType, setAccountType] = useState("seeker");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate(user.account_type === "supporter" ? "/discover" : "/feed", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await register(email, password, name, accountType); toast.success("Welcome to HAPLAP 💛"); }
    catch (err) { toast.error(apiErr(err)); setLoading(false); }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-10 sm:p-12 bg-background paper-bg order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <img src={LOGO} alt="HAPLAP" className="w-12 h-12 rounded-xl" />
            <span className="font-serif font-bold text-2xl text-bronze">HAPLAP</span>
          </Link>
          <h1 className="font-serif text-3xl font-bold text-bronze">Join the jar</h1>
          <p className="text-muted-foreground mt-1 mb-6">Create your free profile. Ask for help, or give it.</p>

          <Button data-testid="google-register-btn" onClick={googleLogin} variant="outline" className="w-full rounded-full py-6 border-border">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5 mr-2" />
            Continue with Google
          </Button>
          <div className="flex items-center gap-3 my-5 text-xs text-muted-foreground">
            <div className="flex-1 h-px bg-border" /> OR <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-copper font-mono">I'm here to…</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <button type="button" data-testid="account-type-seeker" onClick={() => setAccountType("seeker")}
                  className={`p-3 rounded-2xl border text-left transition-all ${accountType === "seeker" ? "border-copper bg-copper/10 ring-1 ring-copper" : "border-border hover:bg-secondary"}`}>
                  <p className="font-semibold text-sm text-bronze">Ask for help 🙏</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">A full public profile to share your story and receive support.</p>
                </button>
                <button type="button" data-testid="account-type-supporter" onClick={() => setAccountType("supporter")}
                  className={`p-3 rounded-2xl border text-left transition-all ${accountType === "supporter" ? "border-sage bg-sage/10 ring-1 ring-sage" : "border-border hover:bg-secondary"}`}>
                  <p className="font-semibold text-sm text-bronze">Help others 💛</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">A private account. You're hidden and anonymous by default.</p>
                </button>
              </div>
            </div>
            <div><Label>Your name</Label>
              <Input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} required className="rounded-xl mt-1" /></div>
            <div><Label>Email</Label>
              <Input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-xl mt-1" /></div>
            <div><Label>Password</Label>
              <div className="relative mt-1">
                <Input data-testid="register-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="rounded-xl pr-11" />
                <button type="button" data-testid="toggle-register-password" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-copper transition-colors">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">At least 6 characters.</p></div>
            <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full bg-copper hover:bg-copper/90 text-white rounded-full py-6">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-center mt-5 text-muted-foreground">
            Already a member? <Link to="/login" className="text-copper font-semibold" data-testid="to-login">Sign in</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-bronze/60 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="font-serif text-3xl leading-tight">Everyone deserves the right to ask for help.</p>
          <p className="mt-3 text-white/80">No judgment. No shame. Just a community that gives.</p>
        </div>
      </div>
    </div>
  );
}
